/**
 * AdminRejectConfirmPage
 * UC-023 - Admin Project Rejection Confirmation
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../components/ui';
import { ArrowLeft, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface ProjectDetail {
  projectId: number;
  name: string;
  description: string;
  status: string;
  useCases: Array<{
    projectUseCaseId: number;
    name: string;
    price: number | string;
    mandatory: boolean;
  }>;
}

export function AdminRejectConfirmPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await api.get<ProjectDetail>(`/projects/${id}`);
        setProject(response);
      } catch (error) {
        console.error('Failed to load project:', error);
        toast.error(t('admin.projects.reject.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id, t]);

  const handleConfirmReject = async () => {
    try {
      setSubmitting(true);

      await api.patch(`/projects/${id}/status`, {
        status: 'Rejected'
      });

      toast.success(t('admin.projects.reject.rejectSuccess'));
      navigate(`/admin/projects`);
    } catch (error) {
      console.error('Failed to reject project:', error);
      toast.error(t('admin.projects.reject.rejectError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/projects/${id}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">{t('admin.projects.reject.notFound')}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('admin.projects.reject.title')}
            </h1>
          </div>
        </div>

        <div className="space-y-6">
          {/* Warning Section */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  {t('admin.projects.reject.warning')}
                </h3>
                <p className="text-red-700">
                  {t('admin.projects.reject.warningMessage')}
                </p>
              </div>
            </div>
          </div>

          {/* Project Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.projects.reject.projectInfo')}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('admin.projects.reject.projectName')}
                </label>
                <p className="text-gray-900 font-medium">{project.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('admin.projects.reject.description')}
                </label>
                <p className="text-gray-700">{project.description}</p>
              </div>
            </div>
          </div>

          {/* Consequences Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-md font-semibold text-yellow-900 mb-3">
              {t('admin.projects.reject.consequences')}
            </h3>
            <ul className="list-disc list-inside space-y-1 text-yellow-800">
              <li>{t('admin.projects.reject.consequence1')}</li>
              <li>{t('admin.projects.reject.consequence2')}</li>
              <li>{t('admin.projects.reject.consequence3')}</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <Button
              onClick={handleCancel}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              <XCircle className="h-4 w-4" />
              {submitting
                ? t('admin.projects.reject.rejecting')
                : t('admin.projects.reject.confirmReject')
              }
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AdminRejectConfirmPage;